import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ClearCreateAppToken, ClearDeleteAppToken, ClearValidateAppTokens, CreateAppToken, DeleteAppToken, ListPasswordAppTokens, ValidateAppTokens } from '../../store/PasswordAppTokenAction';
import AppTokenConfiguration from '../configuration/components/AppTokenConfiguration';
import { CoreAppToken } from '../../models/CoreAppToken';
import { CacheRequestState, EmptyRequestState, RequestState } from '../../models/RequestState';
import { RootState } from '../../store/store';
import StatusCode from '../../util/StatusCode';
import React from 'react';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import ModalComponent from '../misc/ModalComponent';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppTokenType } from '../../models/CoreModels';
import Loading from '../misc/Loading';
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';


enum Crud {
    UNDEFINED = "UNDEFINED",
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE"
}

interface PasswordAppTokenProps {
    isOpen: boolean
    toggleModal: React.MouseEventHandler<any>
}

const PasswordAppToken = (props: PasswordAppTokenProps) => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();

    const [crud, setCrud] = useState<Crud>(Crud.UNDEFINED);
    const [selectedAppToken, setSelectedAppToken] = useState<CoreAppToken | undefined>(undefined);

    const apptokens = useSelector<RootState, CacheRequestState<CoreAppToken[]>>(state => state.passwordAppToken.apptoken)
    const createAppTokenStatus = useSelector<RootState, RequestState<CoreAppToken>>(state => state.passwordAppToken.createAppToken)
    const deleteAppTokenStatus = useSelector<RootState, EmptyRequestState>(state => state.passwordAppToken.deleteAppToken)
    const appTokenValidationRequestState = useSelector<RootState, RequestState<{ id: string, valid: boolean }[]>>(x => x.passwordAppToken.appTokenValidation);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasEditAppToken = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_APPTOKEN);

    useEffect(() => {
        ListPasswordAppTokens()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (apptokens.code === StatusCode.COMPLETE && appTokenValidationRequestState.code === StatusCode.NONE) {
            const ids = apptokens.data.map(x => x.id);
            ValidateAppTokens(ids)(dispatch);
        }
    }, [apptokens.code, appTokenValidationRequestState.code])

    const renderCreatePasswordAppToken = () => {
        return (
            <ModalComponent
                isOpen={crud === Crud.CREATE}
                header={t('passwordTokens.create.title', { ns: 'common' })}
                cancel={t('action.cancel', { ns: 'common' })}
                toggleModal={() => toggleModal()}
            >
                {renderCreateAppTokenBody()}
            </ModalComponent>
        )
    }

    const createAppToken = (type: AppTokenType) => {
        CreateAppToken(type)(dispatch);
    }

    const deleteAppToken = () => {
        if (selectedAppToken?.id) {
            DeleteAppToken(selectedAppToken.id)(dispatch);
        }
    }

    const renderUpdatePasswordAppToken = () => {
        if (selectedAppToken) {
            return (
                <ModalComponent
                    isOpen={crud === Crud.UPDATE}
                    header={t('passwordTokens.update.title', { ns: 'common' })}
                    cancel={t('action.close', { ns: 'common' })}
                    toggleModal={() => toggleModal()}
                    size="lg"
                >
                    {selectedAppToken && <AppTokenConfiguration
                        appToken={selectedAppToken}
                        active={true}
                        disabled={!hasEditAppToken}
                    />}
                </ModalComponent>)
        }
    }

    const renderCreateAppTokenBody = () => {
        switch (createAppTokenStatus.code) {
            case StatusCode.NONE:
                return (<>
                    <p>{t('passwordTokens.create.select', { ns: 'common' })}</p>
                    <Button disabled={!hasEditAppToken} style={{ marginRight: '1.5em' }} onClick={() => createAppToken(AppTokenType.MICROSOFT_APPLICATION)}>Microsoft</Button>
                    <Button disabled={!hasEditAppToken} onClick={() => createAppToken(AppTokenType.GOOGLE_SERVICE_ACCOUNT)}>Google</Button>
                </>)
            case StatusCode.PENDING:
                return <Loading />;
            case StatusCode.COMPLETE:
                return <AppTokenConfiguration
                    appToken={createAppTokenStatus.data}
                    active={true}
                    disabled={false}
                />
            default: return null;
        }
    }

    const renderDeletePasswordAppToken = () => {
        return (
            <ModalComponent
                isOpen={crud === Crud.DELETE}
                header={t('passwordTokens.delete.title', { ns: 'common' })}
                cancel={t('no', { ns: 'common' })}
                toggleModal={() => toggleModal()}
                confirm={t('yes', { ns: 'common' })}
                update={() => deleteAppToken()}
                saveStatus={deleteAppTokenStatus.code}
            >
                <p>{t('passwordTokens.delete.deleteToken', { ns: 'common', tokenName: selectedAppToken?.appRegistration })}</p>
            </ModalComponent>
        )
    }

    const toggleModal = () => {
        setSelectedAppToken(undefined);
        if (crud === Crud.UPDATE || crud === Crud.CREATE) {
            ClearValidateAppTokens()(dispatch);
        }
        setCrud(Crud.UNDEFINED);
        if (createAppTokenStatus.code !== StatusCode.NONE) {
            ClearCreateAppToken()(dispatch);
            ListPasswordAppTokens(true)(dispatch);
        }
        if (deleteAppTokenStatus.code !== StatusCode.NONE) {
            ClearDeleteAppToken()(dispatch);
            ListPasswordAppTokens(true)(dispatch);
        }
    }

    const validate = (id: string) => {
        if (appTokenValidationRequestState.code === StatusCode.COMPLETE) {
            return appTokenValidationRequestState.data.find(x => x.id === id)?.valid
                ? <FontAwesomeIcon icon="check-circle" color="green" className="icon" />
                : <FontAwesomeIcon icon="exclamation-triangle" color="#ffc107" className="icon" />;
        }
        return null
    }

    const renderProfiles = (): InfoObject[] => {
        if (apptokens.code === StatusCode.COMPLETE || (apptokens.code === StatusCode.PENDING)) {
            if (apptokens.data === undefined) {
                return [];
            }
            return apptokens.data.map(x => {
                return ({
                    id: x.id,
                    data: [
                        { value: x.appRegistration },
                        { value: validate(x.id) },
                        {
                            value: <Button disabled={!hasEditAppToken} title={t('action.edit', { ns: 'common' })} className="btn-icon" onClick={() => (setSelectedAppToken(x), setCrud(Crud.UPDATE))}>
                                <FontAwesomeIcon icon="pen" className="icon" /></Button>, className: "edit-icon-btn"
                        },
                        {
                            value: <Button disabled={!hasEditAppToken} title={t('action.remove', { ns: 'common' })} className="btn-icon" onClick={() => (setSelectedAppToken(x), setCrud(Crud.DELETE))}>
                                <FontAwesomeIcon icon="trash" className="icon" /></Button>, className: "edit-icon-btn"
                        }
                    ]
                })
            })
        }
        return [];
    }

    const renderTable = () => (
        <TableHorizontal
            header={[
                { value: t('passwordTokens.table.appregistration', { ns: 'common' }) },
                { value: t('passwordTokens.table.validation', { ns: 'common' }) },
                { value: t('genericTableHeaders.edit', { ns: 'common' }), colSpan: 2, dataLabel: "" }
            ]}
            info={renderProfiles()}
            error={apptokens.code === StatusCode.ERROR}
            searching={apptokens.code === StatusCode.PENDING}
            noResMsg={t('passwordTokens.table.noRes', { ns: 'common' })}
        />
    );

    return (
        <ModalComponent
            isOpen={props.isOpen}
            size="lg"
            header={t('passwordTokens.title', { ns: 'common' })}
            cancel={t('action.close', { ns: 'common' })}
            toggleModal={props.toggleModal}
        >
            {renderTable()}

            <Button disabled={!hasEditAppToken} onClick={() => setCrud(Crud.CREATE)}>{t('passwordTokens.createNew', { ns: 'common' })}</Button>

            {crud === Crud.CREATE && renderCreatePasswordAppToken()}
            {crud === Crud.DELETE && renderDeletePasswordAppToken()}
            {crud === Crud.UPDATE && renderUpdatePasswordAppToken()}

        </ModalComponent>
    )
}

export default PasswordAppToken;
